import { tr, getCurrentLang } from "../i18n/LanguageContext";
const loc = () => (getCurrentLang() === "en" ? "en-GB" : loc());
// Centralized time formatting utilities.
// Backend stores UTC via datetime.utcnow() but FastAPI serializes WITHOUT a "Z" suffix
// (e.g. "2026-04-21T09:41:12.372000"). The browser's Date() parses such strings as
// LOCAL time per ECMAScript spec, which produces -3h drift in UTC+3 timezones (Estonia).
// We normalize by appending "Z" when no timezone is present, so the timestamp is
// correctly interpreted as UTC and Intl methods convert to the user's local timezone.

/**
 * Parse a backend timestamp safely as UTC.
 * Accepts ISO strings (with or without Z), Date instances, or null/undefined.
 * Returns a Date or null.
 */
export function parseBackendDate(input) {
  if (!input) return null;
  if (input instanceof Date) return isNaN(input) ? null : input;
  if (typeof input !== "string") return null;
  // If the string already has a timezone marker (Z, +hh:mm, -hh:mm at the end),
  // trust it; otherwise treat as UTC by appending "Z".
  const hasTz = /Z$|[+-]\d{2}:?\d{2}$/.test(input);
  const iso = hasTz ? input : `${input}Z`;
  const d = new Date(iso);
  return isNaN(d) ? null : d;
}

const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/**
 * Chat-style relative time:
 *   < 60s            → "сейчас"
 *   < 60min          → "N мин"
 *   today            → "HH:mm" in user's local time (ru-RU)
 *   yesterday        → "Вчера"
 *   older this year  → "d MMM"  (e.g. "5 мар")
 *   else             → "dd.mm.yyyy"
 */
export function formatChatTime(input) {
  const d = parseBackendDate(input);
  if (!d) return "";
  const now = new Date();
  const diff = now - d;
  if (diff < 60_000) return tr("сейчас");
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} ${tr("мин")}`;
  if (isSameDay(d, now)) {
    return d.toLocaleTimeString(loc(), { hour: "2-digit", minute: "2-digit" });
  }
  const yest = new Date(now);
  yest.setDate(yest.getDate() - 1);
  if (isSameDay(d, yest)) return tr("Вчера");
  if (d.getFullYear() === now.getFullYear()) {
    return d.toLocaleDateString(loc(), { day: "numeric", month: "short" });
  }
  return d.toLocaleDateString(loc(), { day: "2-digit", month: "2-digit", year: "numeric" });
}

/**
 * Notification-style: short date + local HH:mm.
 *   today          → "Сегодня, HH:mm"
 *   yesterday      → "Вчера, HH:mm"
 *   else this year → "d MMM, HH:mm"
 *   else           → "dd.mm.yyyy, HH:mm"
 */
export function formatNotificationTime(input) {
  const d = parseBackendDate(input);
  if (!d) return "";
  const now = new Date();
  const time = d.toLocaleTimeString(loc(), { hour: "2-digit", minute: "2-digit" });
  if (isSameDay(d, now)) return `${tr("Сегодня")}, ${time}`;
  const yest = new Date(now);
  yest.setDate(yest.getDate() - 1);
  if (isSameDay(d, yest)) return `${tr("Вчера")}, ${time}`;
  if (d.getFullYear() === now.getFullYear()) {
    return `${d.toLocaleDateString(loc(), { day: "numeric", month: "short" })}, ${time}`;
  }
  return `${d.toLocaleDateString(loc(), { day: "2-digit", month: "2-digit", year: "numeric" })}, ${time}`;
}

/**
 * Date-only formatter for admin / approval lists.
 *   today          → "Сегодня"
 *   yesterday      → "Вчера"
 *   else this year → "d MMM"
 *   else           → "dd.mm.yyyy"
 */
export function formatDateRu(input) {
  const d = parseBackendDate(input);
  if (!d) return "";
  const now = new Date();
  if (isSameDay(d, now)) return tr("Сегодня");
  const yest = new Date(now);
  yest.setDate(yest.getDate() - 1);
  if (isSameDay(d, yest)) return tr("Вчера");
  if (d.getFullYear() === now.getFullYear()) {
    return d.toLocaleDateString(loc(), { day: "numeric", month: "short" });
  }
  return d.toLocaleDateString(loc(), { day: "2-digit", month: "2-digit", year: "numeric" });
}
